import { ChangeEvent, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import ContextInput from "~/components/common/contextInput";
import MainButton from "~/components/common/mainButton";
import { useLanguage } from "~/contexts/providers/language";
import englishTranslations from "~/localization/en";
import frenchTranslations from "~/localization/fr";
import { GuestType } from "~/types/event/event.types";
import { getObjectKeys } from "~/utils/functions/general.functions";
import { formatNumber } from "~/utils/functions/string.functions";

import styles from "./index.module.scss";

interface Props {
  type: "book" | "invite";
  eventName: string;
  currentGuests: number;
  allowedGuests: number;
  handleSubmitForm: (guests: GuestType[]) => void;
  isLoading: boolean;
}

const GuestForm = ({
  type,
  eventName,
  currentGuests,
  allowedGuests,
  handleSubmitForm,
  isLoading,
}: Props) => {
  const [disableButton, setDisableButton] = useState(type === "invite");

  const methods = useForm<{ [key: string]: GuestType }>();
  const { language } = useLanguage();
  const accountTexts =
    language === "En"
      ? englishTranslations.accountPage
      : frenchTranslations.accountPage;

  const handleSetDisableButton = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;

    const index = +name.charAt(0);
    const nameToGet = name.includes("lastname") ? "firstname" : "lastname";

    const valueToGet = methods.getValues(`${index}.${nameToGet}`);

    if ((value && !valueToGet) || (valueToGet && !value)) {
      setDisableButton(true);
      return;
    }

    setDisableButton(false);
  };

  const handleSubmit = (data: { [key: string]: GuestType }) => {
    const guests = getObjectKeys(data)
      .map((key) => data[key])
      .filter((guest) => !!(guest.firstname && guest.lastname));
    handleSubmitForm(guests);
  };

  const pluralizeGuest = allowedGuests > 1 ? "s" : "";

  return (
    <div className={styles.guestsModal}>
      <h3>~{eventName}~</h3>
      <br />
      <span>{accountTexts.invitation}</span>
      <div className={styles.guestsModalHead}>
        {`${accountTexts.invitationText1} ${allowedGuests}${
          type === "invite" ? " supplémentaire " : " "
        } ${accountTexts.person}${pluralizeGuest} ${
          accountTexts.invitationText2
        }`}
      </div>
      <FormProvider {...methods}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            methods.handleSubmit(handleSubmit)();
          }}
          className={styles.guestForm}
        >
          {[...Array<never>(allowedGuests)].map((_, i) => (
            <div key={i} className={styles.guestLine}>
              <span>
                {accountTexts.guest} {formatNumber(currentGuests + i + 1)}
              </span>
              <ContextInput<GuestType[]>
                name={`${i}.firstname`}
                label={`guest number ${i}`}
                placeholder={accountTexts.firstname}
                size={7}
                onChange={handleSetDisableButton}
              />
              <ContextInput<GuestType[]>
                name={`${i}.lastname`}
                label={`guest number ${i}`}
                placeholder={accountTexts.lastname}
                size={7}
                onChange={handleSetDisableButton}
              />
            </div>
          ))}
          <br />
          <MainButton
            disabled={disableButton}
            className={styles.submitButton}
            isLoading={isLoading}
          >
            {type === "book"
              ? accountTexts.confirmReservation
              : accountTexts.inviteGuests}
          </MainButton>
        </form>
      </FormProvider>
    </div>
  );
};

export default GuestForm;
